import * as React from 'react';
import { ChevronDown, ChevronRight, Download, Search } from 'lucide-react';
import type { EnderecoInventario, ProdutosDoMapa } from '@/hooks/inventario';
import { cn, moeda, numero, plural } from '@/lib/utils';
import { baixarCsv, carimboArquivo, type ColunaCsv } from '@/lib/csv';
import { Botao } from '@/componentes/ui/botao';
import { Cartao, Kpi } from '@/componentes/ui/primitivos';

/**
 * PREVISÃO DO RESULTADO FINAL, por SKU: como ficaria o estoque se o inventário
 * fosse integrado agora e as próximas contagens não mudassem nada.
 *
 * Por endereço, vale a ÚLTIMA contagem (até a escolhida) em que ele foi
 * contado — bipado ou validado vazio no coletor. Na recontagem e na final só
 * entram os endereços que divergiram OFICIALMENTE na contagem anterior, como
 * no coletor. Endereço nunca contado vai a zero, que é o que o fechamento do
 * PBS faz com ele — e por isso aparece à parte: "falta por não contar" ainda
 * dá para evitar mandando alguém lá.
 *
 * É a mesma conta da divergência ao vivo (foto × soma das coletas, sem
 * lote/validade).
 *
 * ESCOPO COM A FINAL FECHADA não é previsão: o depois é a foto mais a
 * divergência do fechamento final (FECHAMENTO_FINAIS_RESULTADOS), a fonte da
 * consulta RESULTADO_INVENTARIO_CONSOLIDADO e do kardex da integração. A
 * projeção ao vivo chegou a dar R$ 922 mil no inventário 15, cujo oficial era
 * R$ 47 mil: ela não sabe que o fechamento conta por lote/validade nem quais
 * endereços o PBS considerou contados.
 */

type Contagem = 0 | 1 | 2;
type Situacao = 'falta' | 'sobra' | 'igual';

interface Endereco {
  localizador: number;
  escopo: number;
  nome: string;
  antes: number;
  depois: number;
  /** Qual contagem decidiu este endereço; `null` = nunca contado (zera). */
  fonte: Contagem | null;
  /** O depois veio do fechamento final do PBS, não da projeção. */
  oficial: boolean;
  /** O que cada contagem (1ª, recontagem, final) fez com o produto aqui. */
  contagens: ContagemDoEndereco[];
}

interface ContagemDoEndereco {
  k: Contagem;
  /** A contagem pediu este endereço? Na 2ª e na final, só se divergiu antes. */
  noAlvo: boolean;
  /** Foi contado nela — bipado ou validado vazio no coletor. */
  contou: boolean;
  contado: number;
}

interface Sku {
  produto: number;
  descricao: string | null;
  custo: number;
  antes: number;
  depois: number;
  /** Unidades que zeram por endereço nunca contado (já dentro de `depois`). */
  naoContado: number;
  enderecos: Endereco[];
  situacao: Situacao;
  /** Sobra numa vaga e falta noutra: o líquido esconde que mudou de lugar. */
  mudouDeVaga: boolean;
}


/**
 * O valor de cada produto no endereço, pela última contagem que vale para ele.
 *
 * Recontagem ou final JÁ FECHADA que pediu o endereço vale mesmo sem bipe:
 * o PBS fecha em zero, não volta para a contagem anterior (no inventário 15,
 * 1ª 24 → recontagem 23 → final sem bipe fechou em 0). Aberta, sem bipe ainda,
 * a previsão fica com a anterior — alguém ainda pode ir lá.
 */
function projetar(
  e: EnderecoInventario,
  ate: Contagem,
  fechada: (escopo: number, k: Contagem) => boolean,
): { fonte: Contagem | null; valor: Map<number, number> } {
  let fonte: Contagem | null = null;
  for (const k of [0, 1, 2] as Contagem[]) {
    if (k > ate) break;
    const doAlvo = k === 0 || e.divergente[k - 1];
    if (doAlvo && (e.bipado[k] || e.visitado[k] || (k > 0 && fechada(e.escopo, k)))) fonte = k;
  }
  const valor = new Map<number, number>();
  for (const i of e.itens) valor.set(i.produto, fonte === null ? 0 : i.contado[fonte]);
  return { fonte, valor };
}

export function montarResumo(
  enderecos: EnderecoInventario[],
  produtos: ProdutosDoMapa,
  ate: Contagem,
  nomeDe: (localizador: number) => string,
  fechada: (escopo: number, k: Contagem) => boolean,
): Sku[] {
  const porSku = new Map<number, Sku>();
  for (const e of enderecos) {
    // Escopo sem foto não tem "antes" para comparar.
    if (e.comSaldo === null) continue;
    const { fonte, valor } = projetar(e, ate, fechada);
    for (const i of e.itens) {
      const antes = i.esperado ?? 0;
      const oficial = i.divergenciaOficial !== null;
      const depois = oficial ? antes + (i.divergenciaOficial ?? 0) : (valor.get(i.produto) ?? 0);
      if (antes === 0 && depois === 0) continue;
      const p = produtos[i.produto];
      const s =
        porSku.get(i.produto) ??
        ({
          produto: i.produto,
          descricao: p?.descricao ?? null,
          custo: p?.custo ?? 0,
          antes: 0,
          depois: 0,
          naoContado: 0,
          enderecos: [],
          situacao: 'igual',
          mudouDeVaga: false,
        } satisfies Sku);
      s.antes += antes;
      s.depois += depois;
      // No oficial, o que de fato saiu; na projeção, depois = 0 e é o antes inteiro.
      if (fonte === null) s.naoContado += Math.max(0, antes - depois);
      s.enderecos.push({
        localizador: e.localizador,
        escopo: e.escopo,
        nome: nomeDe(e.localizador),
        antes,
        depois,
        fonte,
        oficial,
        contagens: ([0, 1, 2] as Contagem[]).map((k) => ({
          k,
          noAlvo: k === 0 || Boolean(e.divergente[k - 1]),
          contou: e.bipado[k] || e.visitado[k],
          contado: i.contado[k],
        })),
      });
      porSku.set(i.produto, s);
    }
  }
  for (const s of porSku.values()) {
    const d = s.depois - s.antes;
    s.situacao = d < 0 ? 'falta' : d > 0 ? 'sobra' : 'igual';
    s.mudouDeVaga = s.enderecos.some((x) => x.depois > x.antes) && s.enderecos.some((x) => x.depois < x.antes);
    s.enderecos.sort((a, b) => Math.abs(b.depois - b.antes) - Math.abs(a.depois - a.antes));
  }
  return [...porSku.values()];
}

const ESTILO: Record<Situacao, { rotulo: string; ponto: string; texto: string }> = {
  falta: { rotulo: 'Falta', ponto: 'bg-status-divergencia', texto: 'text-status-divergencia' },
  sobra: { rotulo: 'Sobra', ponto: 'bg-primaria', texto: 'text-primaria' },
  igual: { rotulo: 'Sem divergência', ponto: 'bg-status-livre', texto: 'text-status-livre' },
};

export function ResumoPorSku({
  skus,
  ate,
  rotuloContagem,
  inventario,
}: {
  skus: Sku[];
  ate: Contagem;
  rotuloContagem: string;
  inventario: number;
}): JSX.Element {
  const [filtro, definirFiltro] = React.useState<Situacao | 'todos'>('todos');
  const [busca, definirBusca] = React.useState('');
  const [aberto, definirAberto] = React.useState<number | null>(null);

  const total = (lista: Sku[], f: (s: Sku) => number): number => lista.reduce((t, s) => t + f(s), 0);
  const dif = (s: Sku): number => s.depois - s.antes;
  const faltas = skus.filter((s) => s.situacao === 'falta');
  const sobras = skus.filter((s) => s.situacao === 'sobra');
  const iguais = skus.filter((s) => s.situacao === 'igual');
  const faltaR = total(faltas, (s) => -dif(s) * s.custo);
  const sobraR = total(sobras, (s) => dif(s) * s.custo);
  const naoContadoUn = total(skus, (s) => s.naoContado);
  const naoContadoR = total(skus, (s) => s.naoContado * s.custo);
  const antesR = total(skus, (s) => s.antes * s.custo);
  const liquidoR = sobraR - faltaR;
  const linhas = skus.flatMap((s) => s.enderecos);
  const nOficial = linhas.filter((e) => e.oficial).length;
  const tudoOficial = linhas.length > 0 && nOficial === linhas.length;

  /*
   * Busca por SKU, descrição, endereço ou ESCOPO. Quando casa no SKU, ele
   * aparece inteiro; quando casa num endereço ou escopo, o SKU já vem aberto
   * e só com os endereços que casaram — "o que o escopo 2156 vai mexer?".
   */
  const termo = busca.trim().toUpperCase().replace(/^#/, '');
  const casaEndereco = (e: Endereco): boolean =>
    String(e.escopo) === termo || e.nome.toUpperCase().includes(termo) || String(e.localizador) === termo;
  const visiveis = skus
    .filter((s) => filtro === 'todos' || s.situacao === filtro)
    .map((s) => {
      if (!termo) return { s, enderecos: s.enderecos, porFilho: false };
      if (String(s.produto) === termo || (s.descricao ?? '').toUpperCase().includes(termo)) {
        return { s, enderecos: s.enderecos, porFilho: false };
      }
      const enderecos = s.enderecos.filter(casaEndereco);
      return enderecos.length > 0 ? { s, enderecos, porFilho: true } : null;
    })
    .filter((x): x is { s: Sku; enderecos: Endereco[]; porFilho: boolean } => x !== null)
    .sort((a, b) => Math.abs(dif(b.s) * b.s.custo) - Math.abs(dif(a.s) * a.s.custo) || a.s.produto - b.s.produto);


  const exportar = (): void => {
    const colunas: ColunaCsv<Sku>[] = [
      { cabecalho: 'Produto', valor: (s) => s.produto },
      { cabecalho: 'Descrição', valor: (s) => s.descricao ?? '' },
      { cabecalho: 'Situação', valor: (s) => ESTILO[s.situacao].rotulo },
      { cabecalho: 'Estoque antes (foto)', valor: (s) => s.antes },
      { cabecalho: 'Estoque depois (previsão)', valor: (s) => s.depois },
      { cabecalho: 'Diferença', valor: (s) => dif(s) },
      { cabecalho: 'Dos quais por não contar', valor: (s) => -s.naoContado },
      { cabecalho: 'Custo unitário', valor: (s) => s.custo },
      { cabecalho: 'Diferença R$', valor: (s) => Math.round(dif(s) * s.custo * 100) / 100 },
      { cabecalho: 'Mudou de vaga', valor: (s) => (s.mudouDeVaga ? 'sim' : '') },
      { cabecalho: 'Endereços', valor: (s) => s.enderecos.length },
    ];
    baixarCsv(`inventario-${inventario}-previsao-por-sku-${carimboArquivo()}`, visiveis.map((v) => v.s), colunas);
  };

  return (
    <div className="space-y-3">
      {tudoOficial ? (
        <p className="rounded-xl border border-borda bg-superficie2 px-3 py-2 text-[12.5px] text-textoFraco">
          <b className="text-texto">Resultado oficial</b>: todos os escopos fecharam a contagem final, e o depois é a
          foto mais a divergência do fechamento final do PBS — a mesma da consulta Resultado Inventário Consolidado e
          da integração. A contagem escolhida no topo não muda este número.
        </p>
      ) : (
        <p className="rounded-xl border border-borda bg-superficie2 px-3 py-2 text-[12.5px] text-textoFraco">
          <b className="text-texto">Previsão se o inventário fosse integrado agora</b>, com as contagens até a{' '}
          <b className="text-texto">{rotuloContagem.toLowerCase()}</b> (troque no topo). Cada endereço vale pela última
          contagem em que foi contado; o que nunca foi contado vai a zero, como no fechamento do PBS.
          {nOficial > 0
            ? ' Nos escopos que já fecharam a final, vale o resultado oficial do fechamento.'
            : ' O número oficial continua sendo o da integração.'}
        </p>
      )}

      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <Kpi
          rotulo={tudoOficial ? 'Resultado líquido oficial' : 'Resultado líquido previsto'}
          valor={<span className={cn('tabular', liquidoR < 0 ? 'text-status-divergencia' : liquidoR > 0 ? 'text-primaria' : '')}>{moeda(liquidoR)}</span>}
          nota={`sobre ${moeda(antesR)} na foto · ${numero(skus.length)} SKUs`}
          tom={liquidoR === 0 ? 'ok' : 'alerta'}
        />
        <Kpi
          rotulo="Faltas"
          valor={<span className="tabular text-status-divergencia">−{moeda(faltaR)}</span>}
          nota={
            <>
              {numero(faltas.length)} {plural(faltas.length, 'SKU', 'SKUs')} · −{numero(total(faltas, (s) => -dif(s)))} un
              {naoContadoUn > 0 ? (
                <span className="block font-semibold text-status-atencao">
                  {moeda(naoContadoR)} ({numero(naoContadoUn)} un) só por endereço não contado
                </span>
              ) : null}
            </>
          }
          tom={faltas.length > 0 ? 'erro' : 'neutro'}
        />
        <Kpi
          rotulo="Sobras"
          valor={<span className="tabular text-primaria">+{moeda(sobraR)}</span>}
          nota={`${numero(sobras.length)} ${plural(sobras.length, 'SKU', 'SKUs')} · +${numero(total(sobras, dif))} un`}
          tom={sobras.length > 0 ? 'alerta' : 'neutro'}
        />
        <Kpi
          rotulo="Sem divergência"
          valor={numero(iguais.length)}
          nota={`${plural(iguais.length, 'SKU bate', 'SKUs batem')} com a foto${
            iguais.some((s) => s.mudouDeVaga)
              ? ` · ${numero(iguais.filter((s) => s.mudouDeVaga).length)} só mudaram de vaga`
              : ''
          }`}
          tom="ok"
        />
      </div>

      <Cartao className="overflow-hidden p-0">
        <div className="flex flex-wrap items-center gap-1.5 border-b border-borda px-2.5 py-2">
          {(['todos', 'falta', 'sobra', 'igual'] as const).map((f) => {
            const n = f === 'todos' ? skus.length : skus.filter((s) => s.situacao === f).length;
            const ativo = filtro === f;
            return (
              <button
                key={f}
                type="button"
                onClick={() => definirFiltro(f)}
                aria-pressed={ativo}
                className={cn(
                  'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[12px] transition-colors',
                  ativo ? 'border-texto bg-texto text-superficie' : 'border-borda bg-superficie hover:border-bordaForte',
                )}
              >
                {f !== 'todos' ? <span className={cn('h-2.5 w-2.5 rounded-sm', ESTILO[f].ponto)} /> : null}
                {f === 'todos' ? 'Todos' : ESTILO[f].rotulo}
                <span className="tabular font-bold">{numero(n)}</span>
              </button>
            );
          })}
          <div className="relative ml-auto w-full sm:w-[340px]">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-textoFraco" />
            <input
              value={busca}
              onChange={(e) => definirBusca(e.target.value)}
              placeholder="Buscar SKU, descrição, endereço ou escopo (ex.: 2156)"
              title="Busca por SKU, descrição, endereço ou número do escopo — por escopo, mostra só os endereços dele"
              aria-label="Buscar por SKU, descrição, endereço ou escopo"
              className="min-h-[32px] w-full rounded-lg border border-borda bg-superficie pl-8 pr-2 text-[12.5px]"
            />
          </div>
          <Botao variante="secundaria" tamanho="micro" onClick={exportar}>
            <Download className="h-3.5 w-3.5" />
            Baixar CSV
          </Botao>
        </div>

        <div className="max-h-[calc(100vh-360px)] min-h-[200px] overflow-auto">
          <table className="w-full text-[12.5px]">
            <thead className="sticky top-0 z-[1] bg-superficie2 text-left text-[11px] uppercase tracking-wide text-textoFraco">
              <tr>
                <th className="w-6" />
                <th className="px-2 py-1.5">Produto</th>
                <th className="px-2 py-1.5 text-right" title="Saldo da foto do congelamento, nos endereços do inventário">
                  Antes
                </th>
                <th className="px-2 py-1.5 text-right" title="Previsão depois da integração">
                  Depois
                </th>
                <th className="px-2 py-1.5 text-right">Diferença</th>
                <th className="px-2 py-1.5 text-right">R$</th>
                <th className="px-2 py-1.5">Situação</th>
              </tr>
            </thead>
            <tbody>
              {visiveis.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-3 py-6 text-center text-textoSuave">
                    Nenhum SKU neste filtro.
                  </td>
                </tr>
              ) : (
                visiveis.map(({ s, enderecos, porFilho }) => {
                  const d = dif(s);
                  // Busca por endereço ou escopo já abre o SKU, só com o que casou.
                  const abertoAqui = aberto === s.produto || porFilho;
                  return (
                    <React.Fragment key={s.produto}>
                      <tr
                        className={cn(
                          'cursor-pointer border-t border-borda align-top',
                          // Aberto: a linha do SKU "acende" e a barra à esquerda
                          // continua na caixa dos endereços — é o mesmo bloco.
                          abertoAqui
                            ? 'bg-primaria-suave/50 shadow-[inset_3px_0_0_0_rgb(var(--cor-primaria))]'
                            : 'hover:bg-superficie2',
                        )}
                        onClick={() => definirAberto(aberto === s.produto ? null : s.produto)}
                        aria-expanded={abertoAqui}
                      >
                        <td className="px-1 py-1.5 text-textoFraco">
                          {abertoAqui ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                        </td>
                        {/* `w-full max-w-0`: a coluna do produto fica com a sobra da
                            largura e CORTA o nome longo com "…", em vez de empurrar
                            (ou invadir) as colunas de número ao lado. */}
                        <td className="w-full max-w-0 px-2 py-1.5">
                          <div className="flex min-w-0 items-baseline gap-1.5" title={s.descricao ?? ''}>
                            <span className="tabular shrink-0 font-mono font-bold">{s.produto}</span>
                            <span className="min-w-0 truncate">{s.descricao ?? '—'}</span>
                          </div>
                          <span className="block truncate text-[10.5px] text-textoFraco">
                            {numero(s.enderecos.length)} {plural(s.enderecos.length, 'endereço', 'endereços')}
                            {s.naoContado > 0 ? (
                              <b className="ml-1 text-status-atencao">· {numero(s.naoContado)} un em endereço não contado</b>
                            ) : null}
                          </span>
                        </td>
                        <td className="tabular whitespace-nowrap px-2 py-1.5 text-right font-mono">{numero(s.antes)}</td>
                        <td className="tabular whitespace-nowrap px-2 py-1.5 text-right font-mono font-bold">{numero(s.depois)}</td>
                        <td className={cn('tabular whitespace-nowrap px-2 py-1.5 text-right font-mono font-bold', ESTILO[s.situacao].texto)}>
                          {d > 0 ? '+' : ''}
                          {numero(d)}
                        </td>
                        <td className={cn('tabular whitespace-nowrap px-2 py-1.5 text-right font-mono', ESTILO[s.situacao].texto)}>
                          {d === 0 ? '—' : moeda(d * s.custo)}
                        </td>
                        <td className="px-2 py-1.5">
                          <span className="inline-flex items-center gap-1.5">
                            <span className={cn('h-2.5 w-2.5 rounded-sm', ESTILO[s.situacao].ponto)} />
                            {ESTILO[s.situacao].rotulo}
                          </span>
                          {s.mudouDeVaga ? (
                            <span className="block text-[10.5px] font-semibold text-status-atencao">mudou de vaga</span>
                          ) : null}
                        </td>
                      </tr>
                      {abertoAqui ? (
                        <tr className="bg-primaria-suave/50 shadow-[inset_3px_0_0_0_rgb(var(--cor-primaria))]">
                          <td colSpan={7} className="pb-3 pr-3 pt-0.5">
                            {/* Filho recuado à direita, preso ao SKU por uma linha. */}
                            <div className="ml-8 border-l-2 border-primaria/50 pl-3 sm:ml-14">
                              <p className="mb-1 text-[11px] font-semibold text-textoFraco">
                                Endereços do SKU {s.produto} · onde está a diferença
                                {porFilho ? ` · só os que casam com "${busca.trim()}"` : ''}
                              </p>
                            <div className="overflow-hidden rounded-lg border border-borda bg-superficie">
                            <table className="w-full text-[11.5px]">
                              <thead className="bg-superficie2 text-left text-[10.5px] uppercase tracking-wide text-textoFraco">
                                <tr>
                                  <th className="px-2.5 py-1">Endereço</th>
                                  <th className="px-2 py-1">Escopo</th>
                                  <th className="px-2 py-1 text-right" title="Saldo da foto do congelamento">
                                    Antes
                                  </th>
                                  {/* Uma coluna por contagem: o que foi bipado em cada uma. */}
                                  <th className="border-l border-borda px-2 py-1 text-right">1ª</th>
                                  <th className="px-2 py-1 text-right">Recont.</th>
                                  <th className="border-r border-borda px-2 py-1 text-right">Final</th>
                                  <th className="px-2 py-1 text-right" title="Previsão depois da integração">
                                    Depois
                                  </th>
                                  <th className="px-2.5 py-1 text-right">Diferença</th>
                                </tr>
                              </thead>
                              <tbody>
                                {enderecos.map((e) => {
                                  const de = e.depois - e.antes;
                                  return (
                                    <tr key={`${e.escopo}:${e.localizador}`} className="border-t border-borda/60">
                                      <td className="px-2.5 py-1">
                                        <span className="font-mono font-bold">{e.nome}</span>
                                        {e.fonte === null ? (
                                          <span className="block text-[10px] font-semibold text-status-atencao">
                                            nunca contado → zera
                                          </span>
                                        ) : null}
                                      </td>
                                      <td className="tabular px-2 py-1">{e.escopo}</td>
                                      <td className="tabular px-2 py-1 text-right font-mono">{numero(e.antes)}</td>
                                      {e.contagens.map((c) => (
                                        <CelulaContagem
                                          key={c.k}
                                          c={c}
                                          antes={e.antes}
                                          vale={c.k === e.fonte}
                                          fora={c.k > ate}
                                          primeira={c.k === 0}
                                          ultima={c.k === 2}
                                        />
                                      ))}
                                      <td className="tabular px-2 py-1 text-right font-mono font-bold">{numero(e.depois)}</td>
                                      <td
                                        className={cn(
                                          'tabular px-2.5 py-1 text-right font-mono font-bold',
                                          de < 0 ? 'text-status-divergencia' : de > 0 ? 'text-primaria' : 'text-status-livre',
                                        )}
                                      >
                                        {de === 0 ? '✓' : `${de > 0 ? '+' : ''}${numero(de)}`}
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                            </div>
                            <p className="mt-1 text-[10.5px] text-textoFraco">
                              Nas colunas das contagens: o que foi bipado, na cor da diferença contra o antes.{' '}
                              <span className="inline-block rounded px-1 font-bold ring-2 ring-primaria/70">n</span> é a
                              contagem que vale na previsão · "a contar" = pedida e ainda não contada · "—" = não pedida
                              (não divergiu na anterior).
                            </p>
                            </div>
                          </td>
                        </tr>
                      ) : null}
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        <p className="border-t border-borda px-3 py-1.5 text-[11px] text-textoFraco">
          Clique no SKU para ver endereço por endereço. Ordenado pelo maior R$ de diferença.
          {ate > 0 ? ' Na recontagem e na final, só os endereços que divergiram oficialmente antes são recontados.' : ''}
        </p>
      </Cartao>
    </div>
  );
}

/**
 * O que UMA contagem bipou no endereço — uma coluna por contagem, para ler na
 * horizontal: foto → 1ª → recontagem → final → depois.
 *
 *   número     bipado, na cor da diferença contra a foto (verde bateu,
 *              vermelho a menos, roxo a mais);
 *   "a contar" a contagem pediu o endereço e ninguém contou ainda;
 *   "—"        a contagem não pediu (não divergiu na anterior).
 *
 * A que VALE na previsão ganha moldura; as depois da contagem escolhida no
 * topo ficam esmaecidas.
 */
function CelulaContagem({
  c,
  antes,
  vale,
  fora,
  primeira,
  ultima,
}: {
  c: ContagemDoEndereco;
  antes: number;
  vale: boolean;
  fora: boolean;
  primeira: boolean;
  ultima: boolean;
}): JSX.Element {
  const d = c.contado - antes;
  return (
    <td
      className={cn(
        'tabular px-2 py-1 text-right font-mono',
        primeira && 'border-l border-borda',
        ultima && 'border-r border-borda',
        fora && 'opacity-45',
      )}
      title={
        !c.noAlvo
          ? 'Não pedida: o endereço não divergiu na contagem anterior'
          : !c.contou
            ? 'Pedida, ainda não contada'
            : `Bipado ${c.contado} · antes ${antes}${vale ? ' · é a que vale na previsão' : ''}`
      }
    >
      {!c.noAlvo ? (
        <span className="text-textoSuave">—</span>
      ) : !c.contou ? (
        <span className="text-[10.5px] italic text-textoFraco">a contar</span>
      ) : (
        <span
          className={cn(
            'inline-block min-w-[28px] rounded px-1 font-bold',
            d < 0 ? 'text-status-divergencia' : d > 0 ? 'text-primaria' : 'text-status-livre',
            vale && 'ring-2 ring-primaria/70',
          )}
        >
          {numero(c.contado)}
        </span>
      )}
    </td>
  );
}
